/**
 * Check if the input value is an Error object and return a new Error object with
 * the optional provided error message if necessary
 *
 * @param err_obj The potential error object
 * @param default_error_message An optional error message to use if the input value is not an Error object
 * @returns An Error object
 */
export function getErrorObject(err_obj: unknown, default_error_message = 'An error has occurred') {
	if (err_obj instanceof Error) {
		return err_obj
	}

	return new Error(typeof err_obj === 'string' ? err_obj : default_error_message)
}

export function getErrorMessage(err: unknown, default_error_message = 'An error has occurred') {
	const err_obj = getErrorObject(err, default_error_message)

	return err_obj.message
}



// Types for serialized error output
interface SerializedError {
  name: string;
  message: string;
  stack?: string;
  cause?: unknown;
  code?: string | number;
}

interface SerializedUnknownRejection {
  message: string;
  value: unknown;
}

export type SerializedResult = SerializedError | SerializedUnknownRejection;

// Type guard to check if something has a `code` property (e.g. Node system errors)
function hasErrorCode(err: Error): err is Error & { code: string | number } {
	return 'code' in err && (typeof (err as any).code === 'string' || typeof (err as any).code === 'number');
}

export function serializeError(err: unknown): SerializedResult {
	if (err instanceof Error) {
		const result: SerializedError = {
			name: err.name,
			message: err.message,
			stack: err.stack,
			cause: err.cause,
		}
		if (hasErrorCode(err)) {
			result.code = err.code;
		}
		return result
	}

	// Non-Error rejection — string, object, undefined, etc.
	return {
		message: 'Non-Error rejection',
		value: err,
	}
}